(() => {
  const apiBase = (() => {
    const host = location.host.split(':')[0];
    switch (host) {
      case '127.0.0.1': return '//127.0.0.1:8080';
      case 'localhost': return '//localhost:8080';
      case 'delishmall-dev.myshopify.com': return '//dev.api.admin.delishmall.com';
      case 'delishmall.com': return '//api.admin.delishmall.com';
      default: return '';
    }
  })();

  const DATE_PREFIX = 'delivery_date_';
  const TIME_PREFIX = 'delivery_time_';

  /** 選択可能日時の判定は assets/delivery-rules.js に抽出済み */
  const getRules = () => (typeof window !== 'undefined' ? window.DelishDeliveryRules : null) || null;

  const postJSON = async (url, bodyObj) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`POST ${url} failed: ${res.status}`);
    return res.json();
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`GET /cart.js failed: ${res.status}`);
    return res.json();
  };

  /* ======================================================================
   * 販売元ごとの配送ルール取得（/users/shipping）
   * 成功結果のみキャッシュ。失敗はキャッシュせず、再検証時にリトライさせる。
   * ==================================================================== */
  const shippingCache = new Map();

  const fetchShipping = (vendor) => {
    const cached = shippingCache.get(vendor);
    if (cached) return cached;

    const pending = (async () => {
      try {
        const url = `${apiBase}/users/shipping?shop_name=${encodeURIComponent(vendor)}`;
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) throw new Error('shipping api error');
        return { ok: true, data: await res.json() };
      } catch (e) {
        return { ok: false, error: e };
      }
    })();

    shippingCache.set(vendor, pending);
    pending.then((r) => {
      if (!r.ok) shippingCache.delete(vendor);
    });
    return pending;
  };

  /** Liquid が生成した metaobject 由来の配送不可期間（現行踏襲） */
  const getUnavailableRange = (vendor) =>
    (typeof vendorUnavailableRanges !== 'undefined'
      ? vendorUnavailableRanges[String(vendor).trim()]
      : null) || null;

  /* --- [追加] AnyGiftの状態に合わせて表示内容を切り替える --- */
  const updateAnyGiftDisplay = (isGift) => {
    // 配送日時選択エリア
    document.querySelectorAll('tr.cart-vendor__delivery').forEach(el => {
      const dateArea = el.querySelector('.cart__delivery-date');
      const availableNotes = el.querySelector('.cart__delivery-date-available');

      if (dateArea) {
        dateArea.style.display = isGift ? 'none' : '';
      }
      if (availableNotes) {
        availableNotes.style.display = isGift ? 'none' : '';
      }
    });
    // eギフト用メッセージエリア
    document.querySelectorAll('.anygift-message-container').forEach(el => {
      el.style.display = isGift ? 'block' : 'none';
    });
    // VIP/ポイント欄
    document.querySelectorAll('.cart__vip').forEach(el => {
      el.style.setProperty('display', isGift ? 'none' : '', isGift ? 'important' : '');
    });
  };

  /* --- [追加] AnyGiftのボタン変化を監視 --- */
  const initAnyGiftObserver = () => {
    const findAndObserve = () => {
      const btns = document.querySelectorAll('[class*="anygift-option-button"]');
      if (btns.length < 2) return false;

      const observer = new MutationObserver(() => {
        const isEgiftSelected = btns[1].className.includes('active');
        updateAnyGiftDisplay(isEgiftSelected);
      });

      btns.forEach(b => observer.observe(b, { attributes: true }));
      updateAnyGiftDisplay(btns[1].className.includes('active'));
      return true;
    };

    let count = 0;
    const interval = setInterval(() => {
      if (findAndObserve() || count > 40) clearInterval(interval);
      count++;
    }, 200);
  };

  const hideDeliveryDate = (vendor) => {
    const vendorElm = document.querySelector(`tr.cart-vendor__delivery[data-cart-vendor="${CSS.escape(vendor)}"]`);
    if (!vendorElm) return;
    vendorElm.querySelector('.cart__delivery-date')?.classList.add('visually-hidden');
    vendorElm.querySelector('.cart__delivery-date-available')?.classList.add('visually-hidden');
    vendorElm.querySelector('.cart__delivery-date-unavailable')?.classList.remove('visually-hidden');
  };

  const buildOptionsForVendor = async (vendor) => {
    const rules = getRules();
    if (!rules) {
      hideDeliveryDate(vendor);
      return;
    }

    const result = await fetchShipping(vendor);
    if (!result.ok) {
      hideDeliveryDate(vendor);
      return;
    }
    const data = result.data;

    if (!rules.supportsDeliveryDate(data)) {
      hideDeliveryDate(vendor);
      return;
    }

    const deliveryRow = document.querySelector(`tr.cart-vendor__delivery[data-cart-vendor="${CSS.escape(vendor)}"]`);
    if (!deliveryRow) return;

    // NOTE: キーのエンコード方式は Liquid 側(url_encode)と揃っていないが、
    //       既存カートの属性キーとの対応が切れるため今回は現行踏襲。CLAUDE.md 参照。
    const encodedVendor = encodeURIComponent(vendor).replace(/\+/g, '%20');
    const dateSelect = deliveryRow.querySelector(`[data-cart-key="${DATE_PREFIX}${encodedVendor}"]`);
    const timeSelect = deliveryRow.querySelector(`[data-cart-key="${TIME_PREFIX}${encodedVendor}"]`);
    if (!dateSelect || !timeSelect) return;

    const selectedDate = dateSelect.getAttribute('data-cart-date') || '';
    const selectedTime = timeSelect.getAttribute('data-cart-time') || '';

    dateSelect.innerHTML = '<option value="">指定なし</option>';
    rules.buildSelectableDates(data, getUnavailableRange(vendor), new Date()).forEach((dateText) => {
      const opt = document.createElement('option');
      opt.value = dateText;
      opt.textContent = dateText;
      if (selectedDate === dateText) opt.selected = true;
      dateSelect.appendChild(opt);
    });

    timeSelect.innerHTML = '<option value="">指定なし</option>';
    rules.buildSelectableTimes(data).forEach((t) => {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      if (selectedTime === t) opt.selected = true;
      timeSelect.appendChild(opt);
    });
  };

  const getDeliveryRows = () =>
    [...document.querySelectorAll('tr.cart-vendor__delivery[data-cart-vendor]')];

  const initBuildAll = () => {
    const vendors = [...new Set(getDeliveryRows().map(el => el.getAttribute('data-cart-vendor')).filter(Boolean))];
    return Promise.all(vendors.map(v => buildOptionsForVendor(v).catch(() => hideDeliveryDate(v))));
  };

  /* ======================================================================
   * 期限切れ検証
   *
   * カート属性はサーバ側に保持されるため、選択時点では正当だった値が
   * 日をまたいで生き残る。さらに buildOptionsForVendor は select を作り直す
   * だけで change を発火しないため、画面は「指定なし」に見えるのに
   * サーバ側は過去日を保持したままになる。ここでその乖離を潰す。
   *
   * 判定結果:
   *   ok           … 属性が空、または現在も選択可能
   *   invalid      … 選択不可になった → 属性を空にして再選択を促す / 決済ブロック
   *   unselectable … 販売元が日時指定に対応しなくなった → 属性を空にするだけ
   *                  （再選択の余地が無いためブロックしない）
   *   unverifiable … 値はあるが検証できない（API 失敗 / ルール未ロード）
   *                  → 値は消さずに決済ブロック（fail-close）
   * ==================================================================== */
  const ALERT_CLASS = 'cart__delivery-alert';
  const ROW_ALERT_CLASS = 'cart-vendor__delivery--alert';
  const ROW_ERROR_CLASS = 'cart-vendor__delivery--alert-error';

  const attrKeyFromInputName = (el) => {
    if (!el) return null;
    const name = el.getAttribute('name') || '';
    const m = name.match(/^attributes\[(.+)\]$/);
    return m ? m[1] : null;
  };

  /**
   * 属性キーは Liquid が出力した data-cart-key / name をそのまま読む。
   * JS 側で再導出するとエンコード方式の差でキーを取り違えるため。
   */
  const getRowKeys = (row) => {
    const dateSelect = row.querySelector(`select[data-cart-key^="${DATE_PREFIX}"]`);
    const timeSelect = row.querySelector(`select[data-cart-key^="${TIME_PREFIX}"]`);
    if (dateSelect && timeSelect) {
      return {
        dateKey: dateSelect.getAttribute('data-cart-key'),
        timeKey: timeSelect.getAttribute('data-cart-key'),
        dateSelect,
        timeSelect,
      };
    }
    // 「日時指定不可」タグのみの販売元は hidden input だけが出力される
    return {
      dateKey: attrKeyFromInputName(row.querySelector(`input[type="hidden"][name^="attributes[${DATE_PREFIX}"]`)),
      timeKey: attrKeyFromInputName(row.querySelector(`input[type="hidden"][name^="attributes[${TIME_PREFIX}"]`)),
      dateSelect: null,
      timeSelect: null,
    };
  };

  const clearAlert = (row) => {
    row.querySelector(`.${ALERT_CLASS}`)?.remove();
    row.classList.remove(ROW_ALERT_CLASS, ROW_ERROR_CLASS);
  };

  /**
   * @param {'error'|'notice'} kind
   * @param {Array<{text: string, strong?: boolean}>} segments
   */
  const renderAlert = (row, kind, title, segments) => {
    const td = row.querySelector('td');
    if (!td) return;

    let box = row.querySelector(`.${ALERT_CLASS}`);
    if (!box) {
      box = document.createElement('div');
      box.className = ALERT_CLASS;
      box.setAttribute('role', 'alert');
      td.insertBefore(box, td.firstChild);
    }
    box.textContent = '';
    box.dataset.kind = kind;

    const titleEl = document.createElement('p');
    titleEl.className = `${ALERT_CLASS}__title`;
    titleEl.textContent = title;
    box.appendChild(titleEl);

    const bodyEl = document.createElement('p');
    bodyEl.className = `${ALERT_CLASS}__body`;
    segments.forEach((seg) => {
      // textContent のみで組み立てる（カート属性値をそのまま HTML にしない）
      if (seg.strong) {
        const strong = document.createElement('strong');
        strong.textContent = seg.text;
        bodyEl.appendChild(strong);
      } else {
        bodyEl.appendChild(document.createTextNode(seg.text));
      }
    });
    box.appendChild(bodyEl);

    row.classList.add(ROW_ALERT_CLASS);
    row.classList.toggle(ROW_ERROR_CLASS, kind === 'error');
  };

  const inspectRow = async (row, attributes) => {
    const vendor = row.getAttribute('data-cart-vendor') || '';
    const keys = getRowKeys(row);
    const savedDate = keys.dateKey ? (attributes[keys.dateKey] || '') : '';
    const savedTime = keys.timeKey ? (attributes[keys.timeKey] || '') : '';

    const base = { row, vendor, keys, savedDate, savedTime };

    if (!savedDate && !savedTime) return { ...base, state: 'ok' };

    const rules = getRules();
    if (!rules) return { ...base, state: 'unverifiable' };

    // select が無い = 現在は日時指定不可の販売元。再選択の余地が無い
    if (!keys.dateSelect) return { ...base, state: 'unselectable' };

    const result = await fetchShipping(vendor);
    if (!result.ok) return { ...base, state: 'unverifiable' };
    if (!rules.supportsDeliveryDate(result.data)) return { ...base, state: 'unselectable' };

    const dates = rules.buildSelectableDates(result.data, getUnavailableRange(vendor), new Date());
    const times = rules.buildSelectableTimes(result.data);

    const dateOk = rules.isStillSelectable(savedDate, dates);
    const timeOk = rules.isStillSelectable(savedTime, times);
    if (dateOk && timeOk) return { ...base, state: 'ok' };

    return { ...base, state: 'invalid', dateOk, timeOk };
  };

  const applyVerdict = (verdict, attrsToClear) => {
    const { row, keys, savedDate, savedTime } = verdict;
    const rules = getRules();

    if (verdict.state === 'ok') {
      clearAlert(row);
      return false; // ブロックしない
    }

    if (verdict.state === 'unverifiable') {
      // 値は消さない。検証できない値を持ったまま進ませないだけ
      renderAlert(row, 'notice', 'お届け希望日時を確認できません', [
        { text: '配送日時の確認ができません。時間をおいて再度お試しください。' },
      ]);
      return true;
    }

    if (verdict.state === 'unselectable') {
      if (keys.dateKey) attrsToClear[keys.dateKey] = '';
      if (keys.timeKey) attrsToClear[keys.timeKey] = '';
      if (keys.dateSelect) keys.dateSelect.value = '';
      if (keys.timeSelect) keys.timeSelect.value = '';

      const segments = [{ text: 'こちらの販売元は現在お届け日時のご指定を承っておりません。' }];
      if (savedDate) {
        segments.push({ text: '以前ご指定いただいた ' });
        segments.push({ text: rules ? rules.toPlainDateText(savedDate) : savedDate, strong: true });
        segments.push({ text: ' の指定は解除されました。' });
      }
      renderAlert(row, 'notice', 'お届け希望日時の指定を解除しました', segments);
      return false; // 再選択できないのでブロックしない
    }

    // invalid
    if (keys.dateKey) attrsToClear[keys.dateKey] = '';
    if (keys.timeKey) attrsToClear[keys.timeKey] = '';
    if (keys.dateSelect) keys.dateSelect.value = '';
    if (keys.timeSelect) keys.timeSelect.value = '';

    const segments = [];
    if (savedDate) {
      segments.push({ text: '元は ' });
      segments.push({ text: rules ? rules.toPlainDateText(savedDate) : savedDate, strong: true });
      segments.push({ text: ' をご指定でしたが、現在お選びいただけません。再度ご選択ください。' });
    } else if (savedTime) {
      segments.push({ text: '元は ' });
      segments.push({ text: savedTime, strong: true });
      segments.push({ text: ' をご指定でしたが、現在お選びいただけません。再度ご選択ください。' });
    }
    renderAlert(row, 'error', 'お届け希望日時を再度ご選択ください', segments);
    return true;
  };

  /* ---- チェックアウト導線のブロック ---- */
  let checkoutBlocked = false;
  let validationSettled = false;

  const CTA_MESSAGE_CLASS = 'cart__delivery-block-message';

  const setCheckoutBlocked = (blocked) => {
    checkoutBlocked = blocked;

    document.querySelectorAll('#checkout, .cart__checkout-button').forEach((el) => {
      if (blocked) {
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('cart__checkout-button--blocked');
        if (el.tagName === 'BUTTON' && !el.disabled) {
          el.disabled = true;
          el.dataset.deliveryBlocked = '1';
        }
      } else {
        el.removeAttribute('aria-disabled');
        el.classList.remove('cart__checkout-button--blocked');
        // 自分が無効化したものだけ戻す（cart == empty 由来の disabled は触らない）
        if (el.dataset.deliveryBlocked === '1') {
          el.disabled = false;
          delete el.dataset.deliveryBlocked;
        }
      }
    });

    document.querySelectorAll(`.${CTA_MESSAGE_CLASS}`).forEach(el => el.remove());
    if (!blocked) return;

    document.querySelectorAll('.cart__ctas').forEach((cta) => {
      const msg = document.createElement('p');
      msg.className = CTA_MESSAGE_CLASS;
      msg.setAttribute('role', 'alert');
      msg.textContent = 'お届け希望日時をご確認のうえ、再度ご選択ください。';
      cta.appendChild(msg);
    });
  };

  const initCheckoutGuard = () => {
    // 通常導線: form#cart の submit（ログイン時のチェックアウトボタン）
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart') return;

      const submitter = e.submitter;
      const isCheckout = !submitter || submitter.name === 'checkout' || submitter.id === 'checkout';
      if (!isCheckout) return;

      if (checkoutBlocked) {
        e.preventDefault();
        focusFirstAlert();
        return;
      }
      if (!validationSettled) {
        e.preventDefault();
        alert('お届け希望日時を確認しています。数秒後にもう一度お試しください。');
      }
    }, true);

    // 未ログイン時はチェックアウトが <a> になる
    document.addEventListener('click', (e) => {
      const el = e.target.closest('#checkout, .cart__checkout-button');
      if (!el) return;
      if (!checkoutBlocked) return;
      e.preventDefault();
      e.stopPropagation();
      focusFirstAlert();
    }, true);
  };

  const focusFirstAlert = () => {
    try {
      const row = document.querySelector(`tr.${ROW_ERROR_CLASS}`) || document.querySelector(`tr.${ROW_ALERT_CLASS}`);
      if (!row) return;
      row.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      row.querySelector('select[data-cart-key]')?.focus({ preventScroll: true });
    } catch (err) {
      // スクロール/フォーカスの失敗で決済ブロック自体が壊れないようにする
      console.error('focusFirstAlert failed', err);
    }
  };

  let validationRun = 0;

  const runValidation = async (knownCart) => {
    const runId = ++validationRun;
    const rows = getDeliveryRows();
    if (rows.length === 0) {
      if (runId === validationRun) {
        validationSettled = true;
        setCheckoutBlocked(false);
      }
      return;
    }

    let cart = knownCart;
    if (!cart) {
      try {
        cart = await fetchCart();
      } catch (err) {
        console.error('runValidation: /cart.js の取得に失敗', err);
        // カートが読めない = 検証できない。値を持っている可能性がある以上ブロックする
        if (runId === validationRun) {
          validationSettled = true;
          setCheckoutBlocked(true);
        }
        return;
      }
    }

    const attributes = cart.attributes || {};
    const verdicts = await Promise.all(rows.map(row => inspectRow(row, attributes)));
    if (runId !== validationRun) return; // 新しい検証に追い越された

    const attrsToClear = {};
    let blocked = false;
    verdicts.forEach((v) => {
      if (applyVerdict(v, attrsToClear)) blocked = true;
    });

    if (Object.keys(attrsToClear).length > 0) {
      try {
        await postJSON('/cart/update.js', { attributes: attrsToClear });
        Object.keys(attrsToClear).forEach((k) => { attributes[k] = ''; });
      } catch (err) {
        console.error('runValidation: 期限切れ属性の消去に失敗', err);
        // 消せていない = 過去日が残っている。安全側に倒してブロック
        blocked = true;
      }
    }

    if (runId !== validationRun) return;
    validationSettled = true;
    setCheckoutBlocked(blocked);
  };

  const initAttributeSync = () => {
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (!t.hasAttribute('data-cart-key')) return;

      const key = t.getAttribute('data-cart-key');
      const value = (t.type === 'checkbox')
        ? (t.checked ? '1' : '0')
        : t.value;

      const posted = postJSON('/cart/update.js', { attributes: { [key]: value } });
      posted.catch(console.error);

      // 配送日時を選び直したら、その場で再検証してブロックを解除する
      if (key && (key.startsWith(DATE_PREFIX) || key.startsWith(TIME_PREFIX))) {
        posted
          .then(() => runValidation())
          .catch(() => {});
      }
    });
  };

  //add 26.4.9
  const cleanupStaleDeliveryAttributes = async () => {
    const cart = await fetchCart();

    const activeVendorKeys = new Set(
      (cart.items || [])
        .map(item => item.vendor)
        .filter(Boolean)
        .flatMap(vendor => {
          const encoded = encodeURIComponent(vendor);
          return [
            `${DATE_PREFIX}${encoded}`,
            `${TIME_PREFIX}${encoded}`
          ];
        })
    );

    const attrsToClear = {};

    Object.keys(cart.attributes || {}).forEach((key) => {
      const isDeliveryKey =
        key.startsWith(DATE_PREFIX) || key.startsWith(TIME_PREFIX);

      if (!isDeliveryKey) return;

      if (!activeVendorKeys.has(key)) {
        attrsToClear[key] = '';
      }
    });

    if (Object.keys(attrsToClear).length > 0) {
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: attrsToClear
        }),
        credentials: 'same-origin',
      });

      if (!updateRes.ok) {
        throw new Error(`POST /cart/update.js failed: ${updateRes.status}`);
      }

      // 後続の検証が古い値を見ないよう、手元のカートにも反映しておく
      Object.keys(attrsToClear).forEach((k) => { cart.attributes[k] = ''; });
    }

    return cart;
  };

  const initRemoveHandler = () => {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.js-cart-remove');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const itemKey = String(btn.getAttribute('data-cart-item-key') || '');
      const vendor = String(btn.getAttribute('data-cart-vendor') || '');

      if (!itemKey || !vendor) {
        return;
      }

      const vendorItems = [
        ...document.querySelectorAll(`tr.cart-item[data-cart-vendor="${CSS.escape(vendor)}"]`)
      ];

      const remainingCount = vendorItems.filter(row => {
        return row.getAttribute('data-cart-item-key') !== itemKey;
      }).length;


      try {
        const changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          }),
          credentials: 'same-origin',
        });

        if (!changeRes.ok) {
          throw new Error(`POST /cart/change.js failed: ${changeRes.status}`);
        }

        const cartRes = await fetch('/cart.js', {
          credentials: 'same-origin',
        });

        if (!cartRes.ok) {
          throw new Error(`GET /cart.js failed: ${cartRes.status}`);
        }

        const cart = await cartRes.json();

        // カートが空なら delivery系を全部消す
        if (cart.item_count === 0) {
          const allDeliveryAttrs = {};

          Object.keys(cart.attributes || {}).forEach((key) => {
            if (key.startsWith(DATE_PREFIX) || key.startsWith(TIME_PREFIX)) {
              allDeliveryAttrs[key] = '';
            }
          });

          if (Object.keys(allDeliveryAttrs).length > 0) {
            const clearAllRes = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: allDeliveryAttrs
              }),
              credentials: 'same-origin',
            });

            if (!clearAllRes.ok) {
              throw new Error(`POST /cart/update.js failed: ${clearAllRes.status}`);
            }
          }

          location.reload();
          return;
        }

        // カートが空でない場合は、削除したvendorのdelivery系だけ消す
        if (remainingCount === 0) {
          const encodedVendor = encodeURIComponent(vendor);
          const vendorDeliveryAttrs = {};

          Object.keys(cart.attributes || {}).forEach((key) => {
            const isDeliveryKey =
              key.startsWith(DATE_PREFIX) || key.startsWith(TIME_PREFIX);

            if (!isDeliveryKey) return;

            if (key === `${DATE_PREFIX}${encodedVendor}` || key === `${TIME_PREFIX}${encodedVendor}`) {
              vendorDeliveryAttrs[key] = '';
            }
          });

          if (Object.keys(vendorDeliveryAttrs).length > 0) {
            const updateRes = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: vendorDeliveryAttrs
              }),
              credentials: 'same-origin',
            });

            if (!updateRes.ok) {
              throw new Error(`POST /cart/update.js failed: ${updateRes.status}`);
            }
          }
        }

        location.reload();
      } catch (err) {
        console.error(err);
        alert('削除に失敗しました。もう一度お試しください。');
      }
    }, true);
  };
  //add 26.4.9

  const clearVendorAttributesIfNoItemsRemain = async (vendor) => {
    const remains = document.querySelectorAll(`tr.cart-item[data-cart-vendor="${CSS.escape(vendor)}"]`).length;
    if (remains > 0) return;

    const encodedVendor = encodeURIComponent(vendor).replace(/\+/g, '%20').replace(/'/g, "%27").replace(/"/g, "%22");

    await postJSON('/cart/update.js', {
      attributes: {
        [`${DATE_PREFIX}${encodedVendor}`]: '',
        [`${TIME_PREFIX}${encodedVendor}`]: ''
      }
    });

    document.querySelector(`tr.cart-vendor__header[data-cart-vendor="${CSS.escape(vendor)}"]`)?.remove();
    document.querySelector(`tr.cart-vendor__delivery[data-cart-vendor="${CSS.escape(vendor)}"]`)?.remove();
  };

  const initRemoveObserver = () => {
    const target = document.querySelector('#main-cart-items');
    if (!target) return;

    const observer = new MutationObserver(() => {
      const vendors = [...new Set(
        getDeliveryRows()
          .map(el => el.getAttribute('data-cart-vendor'))
          .filter(Boolean)
      )];

      vendors.forEach(v => {
        clearVendorAttributesIfNoItemsRemain(v).catch(console.error);
      });
    });

    observer.observe(target, { childList: true, subtree: true });
  };

  /* ---- 復帰時の再検証（bfcache / タブ復帰 / セクション再描画） ---- */
  const REVALIDATE_INTERVAL_MS = 60 * 1000;
  let lastRevalidatedAt = 0;

  const revalidateAll = async (force) => {
    const now = Date.now();
    if (!force && now - lastRevalidatedAt < REVALIDATE_INTERVAL_MS) return;
    lastRevalidatedAt = now;

    validationSettled = false;
    shippingCache.clear(); // 休業日・リードタイムが変わっている可能性があるため取り直す
    await initBuildAll();
    await runValidation();
  };

  const initRevalidateTriggers = () => {
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) revalidateAll(true).catch(console.error);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') revalidateAll(false).catch(console.error);
    });
  };

  //add 26.4.9
  const init = async () => {
    let cart = null;
    try {
      cart = await cleanupStaleDeliveryAttributes();
    } catch (err) {
      console.error('cleanupStaleDeliveryAttributes failed', err);
    }
  //add 26.4.9
    initAttributeSync();
    initRemoveHandler(); //add 26.4.9
    //initRemoveObserver(); //add 26.4.9
    initAnyGiftObserver();
    initCheckoutGuard();
    initRevalidateTriggers();

    lastRevalidatedAt = Date.now();
    await initBuildAll();
    await runValidation(cart);

    fetch('/cart.js').then(res => res.json()).then(cart => {
      const isGift = cart.items.some(it => it.properties && it.properties['ギフト購入'] === '有効');
      if (isGift) updateAnyGiftDisplay(true);
    });
  };

  const initOnRefresh = () => {
    revalidateAll(true).catch(console.error);
    initAnyGiftObserver();
  };

  document.addEventListener('cart:refresh', initOnRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init().catch(console.error); });
  } else {
    init().catch(console.error);
  }
})();
